/**
 * Service for OREME tags
 * @package services
 * @author Julien Huon <julien.huon@umontpellier.fr>
 */

import { z } from "zod";

/**
 * Tag interface representing a tag in the OREME system
 * @interface Tag
 * @property {string} id - The unique identifier of the tag
 * @property {Object} name - The name of the tag in French and English
 * @property {string} name.fr - The French name
 * @property {string} name.en - The English name
 * @property {Object} description - The description of the tag in French and English
 * @property {string} description.fr - The French description
 * @property {string} description.en - The English description
 * @property {Object} source - The source of the tag in French and English
 * @property {string} source.fr - The French source
 * @property {string} source.en - The English source
 * @property {string} type - The type of the tag
 * @property {string} uri - The URI of the tag
 * @property {boolean} isPublic - Whether the tag is public
 * @property {boolean} isScientific - Whether the tag is scientific
 * @property {boolean} isFavourite - Whether the tag is a favorite
 * @property {boolean} isFeatured - Whether the tag is featured
 * @property {boolean} isGlossary - Whether the tag is a glossary term
 * @property {boolean} hasData - Whether the tag has associated data
 */
interface Tag {
  id: number;
  name: {
    fr: string;
    en: string;
  };
  description: {
    fr: string;
    en: string;
  };
  source: {
    fr: string;
    en: string;
  };
  type: string;
  uri: string;
  isPublic: boolean;
  isScientific: boolean;
  isFavourite: boolean;
  isFeatured: boolean;
  isGlossary: boolean;
  hasData: boolean;
}

/**
 * Zod schema for validating and transforming Tag data from API response
 * @constant {z.ZodSchema<Tag>}
 */
const schema = z
  .object({
    id: z.number(),
    name: z.object({
      fr: z.string(),
      en: z.string(),
    }),
    description: z.object({
      fr: z.string(),
      en: z.string(),
    }),
    source: z.object({
      fr: z.string(),
      en: z.string(),
    }),
    type: z.string(),
    uri: z.string(),
    is_public: z.boolean(),
    is_scientific: z.boolean(),
    is_favourite: z.boolean(),
    is_featured: z.boolean(),
    is_glossary: z.boolean(),
    has_data: z.boolean(),
  })
  .transform((raw): Tag => ({
    id: raw.id,
    name: {
      fr: raw.name.fr,
      en: raw.name.en,
    },
    description: {
      fr: raw.description.fr,
      en: raw.description.en,
    },
    source: {
      fr: raw.source.fr,
      en: raw.source.en,
    },
    type: raw.type,
    uri: raw.uri,
    isPublic: raw.is_public,
    isScientific: raw.is_scientific,
    isFavourite: raw.is_favourite,
    isFeatured: raw.is_featured,
    isGlossary: raw.is_glossary,
    hasData: raw.has_data,
  }));

/**
 * Fetches all tags from the OREME API
 * @async
 * @function fetchTags
 * @returns {Promise<Tag[]>} Array of Tag objects or null if no items found
 * @throws {Error} If the API request fails
 */
const fetchTags = async (): Promise<Tag[]> => {
  const endpoint = `https://api.oreme.org/oreme/tags`;

  const response = await fetch(endpoint, {
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch tags: ${response.status} ${response.statusText}`,
    );
  }

  const json = await response.json();
  const items = json.items ?? [];

  const tags = items.map((raw: unknown) => {
    const parsed = schema.safeParse(raw);
    if (!parsed.success) {
      return null;
    }
    return parsed.data;
  });

  return tags.filter((tag: Tag | null): tag is Tag => tag !== null);
};

export { type Tag, fetchTags };
